import { useState, useEffect } from "react";
import Bugsnag from "@bugsnag/js";
import { VerifyStep } from "./VerifyStep";
import { useUserName } from "hooks";
import { VERIFY_STEPS } from "./constants";
import { useVerifyUserProfileMutation } from "queries";
import { VerifyStatusPopup } from "./VerifyStatusPopup";

interface IFileData {
  file_data: File;
  file_name: string;
}

export interface IInput {
  id: string;
  name: null | string;
  value: string;
  validMessage: null | string;
  isRequired: boolean;
  socialKey?: string;
}

export const VerifyFrom = () => {
  const [file, setFile] = useState<IFileData | null>(null);
  const [inputList, setInputList] = useState<IInput[]>([]);
  const [btnState, setBtnState] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [isShowPopup, setIsShowPopup] = useState(false);
  const [popupStatusText, setPopupStatusText] = useState("");

  const userName = useUserName();
  const verifyUserProfileMutation = useVerifyUserProfileMutation();

  useEffect(() => {
    const validInputs = inputList.filter(({ name }) => name);
    const isRequiredInputs = validInputs.filter(({ isRequired }) => isRequired);
    const isInputs = validInputs.length > 1 && isRequiredInputs.length;

    if (isInputs && file) {
      setBtnState(true);
    } else {
      setBtnState(false);
    }

  }, [file, inputList]);

  const handleUpdateInputs = (inputs: IInput[]) => {
    setInputList(inputs);
  };

  const handleChangeFile = (data: IFileData | null) => {
    setFile(data);
  };

  const handlePopup = (isShowPopup: boolean, message: string) => {
    setPopupStatusText(message);
    setIsShowPopup(isShowPopup);
  };

  const handleTogglePopup = () => {
    setIsShowPopup(prev => !prev);
  };

  const handleVerifySend = async () => {
    const validInputs = inputList.filter(({ name }) => name);
    const formData = new FormData();

    setDisableSubmit(true);

    for (const { name, value } of validInputs) {
      formData.append(`${name}`, value);
    }
    // @ts-ignore
    formData.append("photo", file.file_data);

    try {
      const { message } = await verifyUserProfileMutation.mutateAsync(formData);

      handlePopup(true, message);
      setDisableSubmit(true);
    } catch (e: any) {
      // send error to Bugsnag
      const verifyData = {
        socials: validInputs.map(({ name, value }) => ({ name, value })),
        file: {
          size: file?.file_data?.size,
          type: file?.file_data?.type
        }
      };

      Bugsnag.notify(
        new Error("Verify process error"),
        event => {
          event.context = "VERIFY_ERROR";
          event.addMetadata("Error", {
            message: e?.message,
            code: e?.code,
            verifyData: JSON.stringify(verifyData)
          });
        }
      );

      handlePopup(true, "Something went wrong. Please try later.");
      setDisableSubmit(false);
    }
  };

  const renderSteps = () => {
    return (
      <div className="VerifyPage-Steps">
        {VERIFY_STEPS.map((step) => (
          <VerifyStep
            handleUpdateInputs={handleUpdateInputs}
            handleChangeFile={handleChangeFile}
            inputList={inputList}
            btnState={btnState}
            disableSubmit={disableSubmit}
            key={step.index}
            userName={userName}
            handleVerifySend={handleVerifySend}
            {...step}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="VerifyPage-Steps">
      {renderSteps()}
      <VerifyStatusPopup
        isShowPopup={isShowPopup}
        content={popupStatusText}
        handleTogglePopup={handleTogglePopup}
      />
    </div>
  );
};
